// The unlocked session: keys in memory, the decrypted vault data, and every
// action the UI can take. Nothing here is persisted — locking (or reloading)
// drops the keys, and the next unlock asks for the password and 2FA again.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  aesDecrypt,
  aesDecryptString,
  aesEncrypt,
  aesEncryptString,
  changeMasterPassword,
  deriveKeys,
  DEFAULT_KDF_PARAMS,
  deriveMasterKey,
  deriveSubKey,
  newBackupCodes,
  newVaultKey,
  randomBytes,
  sealLink,
  toB64,
  unlockAccount,
  unwrapVaultKey,
  wrapVaultKey,
  WrongPasswordError,
  type AccountKeys,
  type ProtectedAccount,
} from "../crypto";
import { api, setUnauthorisedHandler } from "../lib/api";
import { copyText, download } from "../lib/clipboard";
import { isoDate } from "../lib/format";
import { passphrase } from "../lib/generator";
import { LINK_EXPIRY, LOCK_AFTER, REQUEST_EXPIRY, subtitleFor, TYPES } from "../lib/itemTypes";
import type {
  Activity,
  Category,
  CredRequest,
  Field,
  Invite,
  Item,
  ItemData,
  ItemType,
  Link,
  LinkPayload,
  Me,
  Member,
  Perm,
  RequestData,
  Settings,
  Vault,
} from "../lib/types";
import type { ToastTone } from "../ui/ds";

export const OWNER_ORG = "Agile Software";

export interface SessionInfo {
  me: Me;
  keys: AccountKeys;
  account: ProtectedAccount;
  settings: Settings;
  backup: { enc: string; used: number[]; saved: boolean };
  passwordChangedAt: number;
}

export interface VaultData {
  vaults: Vault[];
  items: Item[];
  categories: Category[];
  requests: CredRequest[];
  members: Member[];
  invites: Invite[];
  links: Link[];
  activity: Activity[];
}

const EMPTY: VaultData = { vaults: [], items: [], categories: [], requests: [], members: [], invites: [], links: [], activity: [] };

/** What an item editor hands back to save. */
export interface ItemDraft {
  id: string | null;
  vaultId: string;
  type: ItemType;
  title: string;
  catId: string;
  fields: Field[];
  totp: string;
  expires: string;
  remind: number;
  private: boolean;
  file: File | null;
  existingFile?: ItemData["file"];
}

/** Owner previewing what a client sees. Read-only. */
export interface Preview {
  vaultId: string;
  userId: string;
  name: string;
  perm: Exclude<Perm, "owner">;
}

interface ToastState {
  id: number;
  title: string;
  msg?: string;
  tone: ToastTone;
}

// ---------------------------------------------------------------------------
// Raw sync payload → decrypted view
// ---------------------------------------------------------------------------

interface SyncPayload {
  now: number;
  me: Me;
  vaults: { id: string; name: string; kind: "personal" | "client"; contactName: string; contactEmail: string; perm: Perm; wrappedKey: string | null }[];
  items: { id: string; vault_id: string; enc: string; cat_id: string | null; private: number; archived_at: number | null; has_file: number; created_by: string; created_at: number; updated_at: number }[];
  categories: { id: string; vault_id: string; enc: string }[];
  requests: { id: string; vault_id: string; to_user_id: string; to_name: string; enc: string; status: "waiting" | "received" | "cancelled"; item_id: string | null; created_at: number; expires_at: number; resolved_at: number | null }[];
  members: { vault_id: string; user_id: string; perm: Exclude<Perm, "owner">; has_key: number; name: string; email: string; public_key: string; backup_saved: number; last_seen: number | null }[];
  invites: { id: string; vault_id: string; email: string; name: string; perm: Exclude<Perm, "owner">; created_at: number; expires_at: number }[];
  links: { id: string; vault_id: string; item_id: string; passphrase: number; max_views: number; views: number; status: "active" | "revoked"; created_at: number; expires_at: number }[];
  activity: { id: number; vault_id: string; who: string; what: string | null; what_enc: string | null; item_id: string | null; icon: string; created_at: number }[];
}

async function tryDecrypt<T>(key: CryptoKey | null | undefined, enc: string): Promise<T | null> {
  if (!key || !enc) return null;
  try {
    return JSON.parse(await aesDecryptString(key, enc)) as T;
  } catch {
    // Wrong key or tampered ciphertext: leave it out rather than show garbage.
    return null;
  }
}

async function decryptSync(p: SyncPayload, keys: AccountKeys, cache: Map<string, CryptoKey>): Promise<VaultData> {
  const vaults: Vault[] = await Promise.all(
    p.vaults.map(async (v) => {
      let key: CryptoKey | null = null;
      if (v.wrappedKey) {
        key = cache.get(v.wrappedKey) ?? null;
        if (!key) {
          try {
            key = await unwrapVaultKey(keys.privateKey, v.wrappedKey);
            cache.set(v.wrappedKey, key);
          } catch {
            key = null;
          }
        }
      }
      return { id: v.id, name: v.name, kind: v.kind, contactName: v.contactName, contactEmail: v.contactEmail, perm: v.perm, key };
    }),
  );
  const keyOf = new Map(vaults.map((v) => [v.id, v.key]));

  const items = (
    await Promise.all(
      p.items.map(async (r) => {
        const d = await tryDecrypt<ItemData>(keyOf.get(r.vault_id), r.enc);
        if (!d) return null;
        const item: Item = {
          ...d,
          fields: Array.isArray(d.fields) ? d.fields : [],
          id: r.id,
          vaultId: r.vault_id,
          catId: r.cat_id,
          private: !!r.private,
          archivedAt: r.archived_at,
          hasFile: !!r.has_file,
          createdBy: r.created_by,
          createdAt: r.created_at,
          updatedAt: r.updated_at,
        };
        return item;
      }),
    )
  ).filter((x): x is Item => !!x);

  const categories = (
    await Promise.all(
      p.categories.map(async (r) => {
        const d = await tryDecrypt<{ name: string; icon: string }>(keyOf.get(r.vault_id), r.enc);
        return d ? { id: r.id, vaultId: r.vault_id, name: d.name, icon: d.icon } : null;
      }),
    )
  ).filter((x): x is Category => !!x);

  const requests = (
    await Promise.all(
      p.requests.map(async (r) => {
        const d = await tryDecrypt<RequestData>(keyOf.get(r.vault_id), r.enc);
        if (!d) return null;
        const status = r.status === "waiting" && r.expires_at < p.now ? "expired" : r.status;
        const req: CredRequest = {
          ...d,
          id: r.id,
          vaultId: r.vault_id,
          toUserId: r.to_user_id,
          toName: r.to_name,
          status,
          itemId: r.item_id,
          createdAt: r.created_at,
          expiresAt: r.expires_at,
          resolvedAt: r.resolved_at,
        };
        return req;
      }),
    )
  ).filter((x): x is CredRequest => !!x);

  const titleOf = new Map(items.map((i) => [i.id, i.title]));
  const activity: Activity[] = await Promise.all(
    p.activity.map(async (a) => {
      let what = a.what ?? "";
      if (a.what_enc) {
        const k = keyOf.get(a.vault_id);
        try {
          what = k ? await aesDecryptString(k, a.what_enc) : "";
        } catch {
          what = "";
        }
      }
      what = what.replace("{item}", (a.item_id && titleOf.get(a.item_id)) || "a deleted item");
      return { id: a.id, vaultId: a.vault_id, who: a.who, what: what || "made a change", itemId: a.item_id, icon: a.icon, createdAt: a.created_at };
    }),
  );

  return {
    vaults,
    items,
    categories,
    requests,
    members: p.members.map((m) => ({
      vaultId: m.vault_id,
      userId: m.user_id,
      name: m.name,
      email: m.email,
      perm: m.perm,
      hasKey: !!m.has_key,
      publicKey: m.public_key,
      backupSaved: !!m.backup_saved,
      lastSeen: m.last_seen,
    })),
    invites: p.invites.map((i) => ({ id: i.id, vaultId: i.vault_id, email: i.email, name: i.name, perm: i.perm, createdAt: i.created_at, expiresAt: i.expires_at })),
    links: p.links.map((l) => ({ id: l.id, vaultId: l.vault_id, itemId: l.item_id, passphrase: !!l.passphrase, maxViews: l.max_views, views: l.views, status: l.status, createdAt: l.created_at, expiresAt: l.expires_at })),
    activity,
  };
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

type Store = ReturnType<typeof useStoreValue>;
const Ctx = createContext<Store | null>(null);

export function useVault(): Store {
  const s = useContext(Ctx);
  if (!s) throw new Error("useVault outside VaultProvider");
  return s;
}

/** Toasts work before unlock too (sign-in screens), so they live apart. */
const ToastCtx = createContext<(title: string, msg?: string, tone?: ToastTone) => void>(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children, render }: { children: ReactNode; render: (t: ToastState | null, close: () => void) => ReactNode }) {
  const [toast, setToast] = useState<ToastState | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const show = useCallback((title: string, msg?: string, tone: ToastTone = "success") => {
    if (timer.current) clearTimeout(timer.current);
    setToast({ id: Date.now(), title, msg, tone });
    timer.current = setTimeout(() => setToast(null), 3200);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {render(toast, () => setToast(null))}
    </ToastCtx.Provider>
  );
}

export function VaultProvider({ session, onLock, children }: { session: SessionInfo; onLock: (reason?: string) => void; children: ReactNode }) {
  const value = useStoreValue(session, onLock);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

function useStoreValue(initial: SessionInfo, onLock: (reason?: string) => void) {
  const toast = useToast();
  const [session, setSession] = useState(initial);
  const [data, setData] = useState<VaultData>(EMPTY);
  const [loaded, setLoaded] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const keyCache = useRef(new Map<string, CryptoKey>());
  const { me, keys } = session;
  const isOwner = me.role === "owner";
  const settingsRef = useRef(session.settings);
  settingsRef.current = session.settings;

  // ---- lock ----
  const lock = useCallback(
    (reason?: string) => {
      void api("/auth/logout", { method: "POST" }).catch(() => {});
      onLock(reason);
    },
    [onLock],
  );

  useEffect(() => {
    setUnauthorisedHandler(() => onLock("Your session ended. Unlock again to carry on."));
    return () => setUnauthorisedHandler(null);
  }, [onLock]);

  // Auto-lock after the configured idle time.
  useEffect(() => {
    const ms = LOCK_AFTER[session.settings.lock];
    if (!ms) return;
    let last = Date.now();
    const bump = () => {
      last = Date.now();
    };
    const events = ["mousemove", "keydown", "pointerdown", "scroll", "touchstart"] as const;
    events.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    const t = setInterval(() => {
      if (Date.now() - last >= ms) lock("Locked after a period of inactivity.");
    }, 5_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, bump));
      clearInterval(t);
    };
  }, [session.settings.lock, lock]);

  // ---- sync ----
  const refresh = useCallback(async () => {
    let payload = await api<SyncPayload>("/sync");
    let next = await decryptSync(payload, keys, keyCache.current);

    // Owner: hand the vault key to members who have finished setting up (and,
    // if required, saved their backup codes). Their own public key wraps it.
    if (payload.me.role === "owner") {
      const pending = next.members.filter((m) => !m.hasKey && (m.backupSaved || !settingsRef.current.requireBackup));
      let wrapped = 0;
      for (const m of pending) {
        const vault = next.vaults.find((v) => v.id === m.vaultId);
        if (!vault?.key) continue;
        try {
          await api(`/vaults/${m.vaultId}/keys`, { body: { userId: m.userId, wrappedKey: await wrapVaultKey(vault.key, m.publicKey) } });
          wrapped++;
        } catch {
          // Retried on the next sync.
        }
      }
      if (wrapped) {
        payload = await api<SyncPayload>("/sync");
        next = await decryptSync(payload, keys, keyCache.current);
      }
    }
    setData(next);
    setLoaded(true);
    return next;
  }, [keys]);

  useEffect(() => {
    refresh().catch((e: unknown) => toast("Couldn't load your vaults", e instanceof Error ? e.message : undefined, "danger"));
    // Pick up client changes (fulfilled requests, new members) while open.
    const t = setInterval(() => {
      if (document.visibilityState === "visible") refresh().catch(() => {});
    }, 60_000);
    return () => clearInterval(t);
  }, [refresh, toast]);

  // ---- helpers ----
  const vaultKey = useCallback(
    (vaultId: string): CryptoKey => {
      const k = data.vaults.find((v) => v.id === vaultId)?.key;
      if (!k) throw new Error("You don't have the key for this vault yet.");
      return k;
    },
    [data.vaults],
  );
  const enc = useCallback(async (vaultId: string, value: unknown) => aesEncryptString(vaultKey(vaultId), JSON.stringify(value)), [vaultKey]);
  const encText = useCallback(async (vaultId: string, text: string) => aesEncryptString(vaultKey(vaultId), text), [vaultKey]);

  /** Block writes while previewing as a client. */
  const guard = useCallback(() => {
    if (!preview) return false;
    toast("Preview only", "Nothing changes while you're viewing as a client.", "info");
    return true;
  }, [preview, toast]);

  const copy = useCallback(
    async (value: string, what: string) => {
      const ok = await copyText(value, settingsRef.current.clip);
      if (ok) toast(`${what} copied`, settingsRef.current.clip ? "Clears from your clipboard in 30s." : undefined);
      else toast("Couldn't copy", "Your browser blocked clipboard access.", "warning");
    },
    [toast],
  );

  /** Clients' reveals and copies are written to the vault's audit trail. */
  const logAccess = useCallback(
    async (vaultId: string, what: string, itemId: string | null, icon: "eye" | "copy" | "download" | "timer") => {
      if (isOwner || preview) return;
      try {
        await api(`/vaults/${vaultId}/activity`, { body: { whatEnc: await encText(vaultId, what), itemId, icon } });
      } catch {
        // Best effort; never block a copy on the log.
      }
    },
    [isOwner, preview, encText],
  );

  // ---- items ----
  const saveItem = useCallback(
    async (d: ItemDraft): Promise<string> => {
      const isNew = !d.id;
      const fileMeta = d.file ? { name: d.file.name, size: d.file.size, mime: d.file.type || "application/octet-stream" } : d.existingFile;
      const body: ItemData = {
        type: d.type,
        title: d.title.trim(),
        sub: subtitleFor(d.type, d.fields, fileMeta?.name),
        fields: d.fields,
        ...(d.totp.trim() ? { totp: d.totp.trim() } : {}),
        ...(fileMeta ? { file: fileMeta } : {}),
        ...(d.expires ? { expires: d.expires, remind: d.remind } : {}),
      };
      const vault = data.vaults.find((v) => v.id === d.vaultId);
      const logEnc = await encText(d.vaultId, `${isNew ? "added" : "edited"} ${body.title}`);
      let id = d.id;
      const existing = id ? data.items.find((i) => i.id === id) : undefined;
      if (existing && existing.vaultId !== d.vaultId) {
        // Moving vaults = re-encrypt under the new vault's key: create, then delete.
        id = null;
      }
      if (!id) {
        const res = await api<{ id: string }>(`/vaults/${d.vaultId}/items`, {
          body: { enc: await enc(d.vaultId, body), catId: d.catId || null, private: vault?.kind === "client" ? d.private : false, logEnc },
        });
        id = res.id;
        if (existing) {
          if (existing.hasFile && !d.file) {
            // Carry the attachment across: decrypt with the old key, re-encrypt with the new.
            const old = await api<string>(`/items/${existing.id}/file`).catch(() => null);
            if (old) {
              const plain = await aesDecrypt(vaultKey(existing.vaultId), old);
              await api(`/items/${id}/file`, { method: "PUT", raw: await aesEncrypt(vaultKey(d.vaultId), plain) });
            }
          }
          await api(`/items/${existing.id}`, { method: "DELETE", body: { logEnc: await encText(existing.vaultId, `moved ${existing.title} to ${vault?.name ?? "another vault"}`) } });
        }
      } else {
        const patch: Record<string, unknown> = { enc: await enc(d.vaultId, body), catId: d.catId || null, logEnc };
        if (isOwner && vault?.kind === "client") patch.private = d.private;
        await api(`/items/${id}`, { method: "PUT", body: patch });
      }
      if (d.file) {
        const sealed = await aesEncrypt(vaultKey(d.vaultId), new Uint8Array(await d.file.arrayBuffer()));
        await api(`/items/${id}/file`, { method: "PUT", raw: sealed });
      }
      await refresh();
      return id;
    },
    [data.vaults, data.items, enc, encText, isOwner, refresh, vaultKey],
  );

  const setItemPrivate = useCallback(
    async (item: Item, priv: boolean) => {
      if (guard()) return;
      const vault = data.vaults.find((v) => v.id === item.vaultId);
      await api(`/items/${item.id}`, {
        method: "PUT",
        body: { private: priv, icon: priv ? "eye-off" : "users", logEnc: await encText(item.vaultId, `${priv ? "stopped sharing" : "shared"} ${item.title} with ${vault?.name}`) },
      });
      await refresh();
      toast(priv ? `Hidden from ${vault?.name}` : `Shared with ${vault?.name}`, priv ? "Only you can see it. One-time links still work." : "Everyone with access to this vault can see it now.");
    },
    [data.vaults, encText, guard, refresh, toast],
  );

  const setItemArchived = useCallback(
    async (item: Item, on: boolean) => {
      if (guard()) return;
      await api(`/items/${item.id}`, {
        method: "PUT",
        body: { archived: on, icon: on ? "archive" : "archive-restore", logEnc: await encText(item.vaultId, `${on ? "archived" : "unarchived"} ${item.title}`) },
      });
      await refresh();
      toast(on ? `${item.title} archived` : `${item.title} restored`, on ? "Find it under Archived in this vault." : "It’s back in the list.");
    },
    [encText, guard, refresh, toast],
  );

  const deleteItem = useCallback(
    async (item: Item) => {
      if (guard()) return;
      await api(`/items/${item.id}`, { method: "DELETE", body: { logEnc: await encText(item.vaultId, `deleted ${item.title}`) } });
      await refresh();
      toast(`${item.title} deleted`);
    },
    [encText, guard, refresh, toast],
  );

  const downloadFile = useCallback(
    async (item: Item) => {
      if (!item.file) return;
      toast(`Decrypting ${item.file.name}`, "Download starts in a moment.", "info");
      const sealed = await api<string>(`/items/${item.id}/file`);
      download(item.file.name, await aesDecrypt(vaultKey(item.vaultId), sealed), item.file.mime);
      void logAccess(item.vaultId, `downloaded ${item.file.name} from ${item.title}`, item.id, "download");
    },
    [logAccess, toast, vaultKey],
  );

  // ---- categories ----
  const createCategory = useCallback(
    async (vaultId: string, name: string, icon: string) => {
      const res = await api<{ id: string }>(`/vaults/${vaultId}/categories`, {
        body: { enc: await enc(vaultId, { name, icon }), logEnc: await encText(vaultId, `created the category ${name}`) },
      });
      await refresh();
      return res.id;
    },
    [enc, encText, refresh],
  );

  const deleteCategory = useCallback(
    async (vaultId: string, cat: Category, moveTo: string, count: number, toName: string) => {
      await api(`/vaults/${vaultId}/categories/${cat.id}`, {
        method: "DELETE",
        body: { moveTo: moveTo || null, logEnc: await encText(vaultId, `deleted the category ${cat.name}${count ? ` · ${count} moved to ${toName}` : ""}`) },
      });
      await refresh();
    },
    [encText, refresh],
  );

  // ---- vaults & people ----
  const createVault = useCallback(
    async (name: string, contactName: string, contactEmail: string) => {
      const { key } = await newVaultKey();
      const wrappedKey = await wrapVaultKey(key, keys.publicKeyB64);
      const res = await api<{ id: string; inviteUrl?: string; added?: boolean; error?: string }>("/vaults", {
        body: { name, contactName, contactEmail, wrappedKey },
      });
      await refresh();
      return res;
    },
    [keys.publicKeyB64, refresh],
  );

  const invite = useCallback(
    async (vaultId: string, email: string, name: string, perm: Exclude<Perm, "owner">) => {
      const res = await api<{ inviteUrl?: string; added?: boolean }>(`/vaults/${vaultId}/invites`, { body: { email, name, perm } });
      await refresh();
      return res;
    },
    [refresh],
  );

  const revokeInvite = useCallback(
    async (inv: Invite) => {
      if (guard()) return;
      await api(`/vaults/${inv.vaultId}/invites/${inv.id}`, { method: "DELETE" });
      await refresh();
      toast("Invite revoked", `The link sent to ${inv.email} no longer works.`);
    },
    [guard, refresh, toast],
  );

  const setMemberPerm = useCallback(
    async (m: Member, perm: Exclude<Perm, "owner">) => {
      if (guard()) return;
      await api(`/vaults/${m.vaultId}/members/${m.userId}`, { method: "PUT", body: { perm } });
      await refresh();
      toast(`${m.name}: ${perm === "view" ? "view only" : perm === "edit" ? "can edit" : "can add only"}`);
    },
    [guard, refresh, toast],
  );

  const revokeMember = useCallback(
    async (m: Member) => {
      if (guard()) return;
      await api(`/vaults/${m.vaultId}/members/${m.userId}`, { method: "DELETE" });
      await refresh();
      toast("Access revoked", `${m.name}’s sessions have ended.`);
    },
    [guard, refresh, toast],
  );

  // ---- requests ----
  const createRequest = useCallback(
    async (vaultId: string, r: { toUserId: string; toName: string; title: string; type: ItemType; fields: string[]; msg: string; exp: string }) => {
      const body: RequestData = { title: r.title, type: r.type, fields: r.fields, msg: r.msg };
      await api(`/vaults/${vaultId}/requests`, {
        body: { toUserId: r.toUserId, enc: await enc(vaultId, body), ttl: REQUEST_EXPIRY[r.exp], logEnc: await encText(vaultId, `requested ${r.title} from ${r.toName}`) },
      });
      await refresh();
    },
    [enc, encText, refresh],
  );

  const cancelRequest = useCallback(
    async (r: CredRequest) => {
      if (guard()) return;
      await api(`/requests/${r.id}/cancel`, { body: { logEnc: await encText(r.vaultId, `cancelled the request for ${r.title}`) } });
      await refresh();
      toast("Request cancelled");
    },
    [encText, guard, refresh, toast],
  );

  const fulfilRequest = useCallback(
    async (r: CredRequest, values: string[]) => {
      if (guard()) return false;
      const tpl = TYPES[r.type].f;
      const fields: Field[] = r.fields.map((k, i) => {
        const t = tpl.find((x) => x[0] === k);
        return { k, v: values[i] ?? "", ...(t?.[1] ? { s: true } : {}), ...(t?.[2] ? { m: true } : {}) };
      });
      const body: ItemData = { type: r.type, title: r.title, sub: `Added by ${me.name}`, fields };
      await api(`/requests/${r.id}/fulfil`, { body: { enc: await enc(r.vaultId, body), logEnc: await encText(r.vaultId, `added ${r.title} (requested)`) } });
      await refresh();
      return true;
    },
    [enc, encText, guard, me.name, refresh],
  );

  // ---- one-time links ----
  const createLink = useCallback(
    async (item: Item, opts: { exp: string; views: number; usePass: boolean }) => {
      const pass = opts.usePass ? passphrase(3, false) : null;
      const payload: LinkPayload = { title: item.title, type: item.type, fields: item.fields, ...(item.totp ? { totp: item.totp } : {}) };
      const sealed = await sealLink(payload, pass);
      const res = await api<{ id: string; url: string }>(`/vaults/${item.vaultId}/links`, {
        body: {
          itemId: item.id,
          enc: sealed.enc,
          proofHash: sealed.proofHash,
          passphrase: !!pass,
          passSalt: sealed.passSalt,
          maxViews: opts.views,
          ttl: LINK_EXPIRY[opts.exp],
          logEnc: await encText(item.vaultId, `created a one-time link for ${item.title} · ${opts.exp}`),
        },
      });
      await refresh();
      return { url: `${res.url}#${sealed.fragment}`, passphrase: pass };
    },
    [encText, refresh],
  );

  const revokeLink = useCallback(
    async (l: Link) => {
      if (guard()) return;
      const title = data.items.find((i) => i.id === l.itemId)?.title ?? "a deleted item";
      await api(`/links/${l.id}/revoke`, { body: { logEnc: await encText(l.vaultId, `revoked the link for ${title}`) } });
      await refresh();
      toast("Link revoked", "It stops working immediately.");
    },
    [data.items, encText, guard, refresh, toast],
  );

  // ---- account ----
  const updateSettings = useCallback(
    async (patch: Partial<Settings>) => {
      if (guard()) return;
      setSession((s) => ({ ...s, settings: { ...s.settings, ...patch } }));
      try {
        const res = await api<{ settings: Settings }>("/me/settings", { method: "PUT", body: patch });
        setSession((s) => ({ ...s, settings: res.settings }));
      } catch (e) {
        toast("Couldn't save that setting", e instanceof Error ? e.message : undefined, "danger");
      }
    },
    [guard, toast],
  );

  const changePassword = useCallback(
    async (current: string, next: string) => {
      const acct = session.account;
      try {
        await unlockAccount(current, acct);
      } catch (e) {
        if (e instanceof WrongPasswordError) throw new Error("Your current master password isn't right.");
        throw e;
      }
      const { authKeyB64: currentAuthKey } = await deriveKeys(current, acct.kdfSaltB64, acct.kdf);
      const rotated = await changeMasterPassword(next, keys, acct, DEFAULT_KDF_PARAMS);
      const res = await api<{ passwordChangedAt: number }>("/me/password", {
        body: { currentAuthKey, authKey: rotated.authKeyB64, kdf: rotated.kdf, kdfSalt: rotated.kdfSaltB64, protectedSymmetricKey: rotated.protectedSymmetricKey },
      });
      const { authKeyB64: _drop, ...account } = rotated;
      void _drop;
      setSession((s) => ({ ...s, account, passwordChangedAt: res.passwordChangedAt }));
    },
    [keys, session.account],
  );

  const reconfigureTotp = useCallback(async (secret: string, code: string) => {
    await api("/me/totp", { body: { secret, code } });
  }, []);

  const backupCodes = useCallback(async (): Promise<string[]> => {
    if (!session.backup.enc) return [];
    return JSON.parse(await aesDecryptString(keys.userKey, session.backup.enc)) as string[];
  }, [keys.userKey, session.backup.enc]);

  const regenerateBackupCodes = useCallback(async () => {
    const codes = newBackupCodes();
    const encCodes = await aesEncryptString(keys.userKey, JSON.stringify(codes));
    await api("/me/backup-codes", { body: { codes, enc: encCodes } });
    setSession((s) => ({ ...s, backup: { enc: encCodes, used: [], saved: false } }));
    return codes;
  }, [keys.userKey]);

  const markBackupSaved = useCallback(async () => {
    await api("/me/backup-codes/saved", { method: "POST" });
    setSession((s) => ({ ...s, backup: { ...s.backup, saved: true } }));
  }, []);

  /** Export every vault to one file, encrypted with a key from the master password. */
  const exportBackup = useCallback(
    async (password: string) => {
      try {
        await unlockAccount(password, session.account);
      } catch (e) {
        if (e instanceof WrongPasswordError) throw new Error("That master password isn't right.");
        throw e;
      }
      const salt = randomBytes(16);
      const master = await deriveMasterKey(password, salt, DEFAULT_KDF_PARAMS);
      const key = await deriveSubKey(master, salt, "export");
      const plain = {
        format: "agile-vault-export/1",
        exportedAt: new Date().toISOString(),
        vaults: data.vaults
          .filter((v) => v.key)
          .map((v) => ({
            name: v.name,
            kind: v.kind,
            contact: { name: v.contactName, email: v.contactEmail },
            categories: data.categories.filter((c) => c.vaultId === v.id).map(({ id, name, icon }) => ({ id, name, icon })),
            items: data.items
              .filter((i) => i.vaultId === v.id)
              .map(({ type, title, fields, totp, file, expires, remind, catId, private: priv, archivedAt }) => ({ type, title, fields, totp, file, expires, remind, catId, private: priv, archived: !!archivedAt })),
          })),
      };
      const file = {
        format: "agile-vault-export/1",
        kdf: DEFAULT_KDF_PARAMS,
        hkdfInfo: "export",
        salt: toB64(salt),
        data: await aesEncryptString(key, JSON.stringify(plain)),
      };
      const name = `agile-vault-${isoDate(new Date())}.enc`;
      download(name, JSON.stringify(file, null, 2), "application/json");
      return name;
    },
    [data, session.account],
  );

  return useMemo(
    () => ({
      session,
      me,
      isOwner,
      data,
      loaded,
      preview,
      setPreview,
      refresh,
      lock,
      toast,
      copy,
      logAccess,
      guard,
      saveItem,
      setItemPrivate,
      setItemArchived,
      deleteItem,
      downloadFile,
      createCategory,
      deleteCategory,
      createVault,
      invite,
      revokeInvite,
      setMemberPerm,
      revokeMember,
      createRequest,
      cancelRequest,
      fulfilRequest,
      createLink,
      revokeLink,
      updateSettings,
      changePassword,
      reconfigureTotp,
      backupCodes,
      regenerateBackupCodes,
      markBackupSaved,
      exportBackup,
    }),
    [
      session, me, isOwner, data, loaded, preview, refresh, lock, toast, copy, logAccess, guard, saveItem, setItemPrivate,
      setItemArchived, deleteItem, downloadFile, createCategory, deleteCategory, createVault, invite, revokeInvite,
      setMemberPerm, revokeMember, createRequest, cancelRequest, fulfilRequest, createLink, revokeLink, updateSettings,
      changePassword, reconfigureTotp, backupCodes, regenerateBackupCodes, markBackupSaved, exportBackup,
    ],
  );
}
